import { useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import SpreadHistogram from "@/components/SpreadHistogram";
import PriceChart from "@/components/PriceChart";
import DailyTargetCard from "@/components/DailyTargetCard";
import CoinIcon from "@/components/CoinIcon";
import "./Dashboard.css";

const fmtUsd = (n) => {
  const v = Number(n || 0);
  return `${v >= 0 ? "+" : ""}$${Math.abs(v).toFixed(2)}`;
};
const fmtPrice = (n) => {
  const v = Number(n || 0);
  return v < 10 ? v.toFixed(4) : v.toFixed(2);
};

export default function Dashboard() {
  const qc = useQueryClient();

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const { data: prices } = useQuery({
    queryKey: ["prices", settings?.signal_symbol || "ETHUSDT"],
    queryFn: () => api.prices(settings?.signal_symbol || "ETHUSDT"),
    refetchInterval: 5000,
    enabled: !!settings,
  });
  const { data: stats } = useQuery({
    queryKey: ["stats"], queryFn: api.stats, refetchInterval: 12000,
  });
  const { data: openPositions } = useQuery({
    queryKey: ["positions", "OPEN"],
    queryFn: () => api.positions({ status: "OPEN", limit: 5 }),
    refetchInterval: 8000,
  });
  const { data: live, isLoading: liveLoading } = useQuery({
    queryKey: ["dashboard-live"],
    queryFn: () => api.signalsLive({
      min_confidence: 30,
      counter_exchange: settings?.preferred_counter_exchange || "BINANCE",
    }),
    refetchInterval: 15000,
    enabled: !!settings,
  });

  const topSignals = useMemo(() => (live?.signals || []).slice(0, 3), [live]);
  const positions = openPositions || [];
  const bonusPct = Math.round((settings?.default_bonus_rate || 0.1) * 100);
  const tierLabel = settings?.tier || "Regular";
  const heroPnl = stats?.total_net_pnl ?? 0;
  const pendingBonus = stats?.total_bonus_pending ?? 0;

  const scanMut = useMutation({
    mutationFn: () => api.scanFlow(30),
    onSuccess: (data) => {
      toast.success(`${data.count} sinyal kaydedildi`);
      qc.invalidateQueries({ queryKey: ["dashboard-live"] });
    },
    onError: (e) => toast.error(e?.message || "Tarama hatası"),
  });

  const openSignal = async (signal) => {
    try {
      await api.openNow({ symbol: signal.symbol });
      toast.success(`${signal.symbol} pozisyonu açıldı`);
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard-live"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="dashboard-root">
      {/* ===== Strategy ribbon ===== */}
      <div className="dx-strat" data-testid="strategy-ribbon">
        <span className="ic">
          <svg viewBox="0 0 24 24">
            <path d="M12 2v6m0 8v6M2 12h6m8 0h6" strokeLinecap="round" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </span>
        <span className="tx">
          <b>Strateji</b> · Darkex'te ≥5dk · ≥$10 kayıp{" "}
          <span className="ar">→</span> karşı borsada hedge{" "}
          <span className="ar">→</span> bonus = saf kâr
        </span>
        <span className="rt" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span>Tier oranı <b>%{bonusPct}</b></span>
        </span>
      </div>

      {/* ===== HERO ===== */}
      <section className="dx-hero" data-testid="dashboard-hero">
        <div className="dx-hero-l">
          <div className="top">
            <span className="d" /> Net Hedge K/Z · Bugün
          </div>
          <div className={`dx-hero-val ${heroPnl >= 0 ? "up" : "down"}`}
               data-testid="hero-net-pnl">
            {fmtUsd(heroPnl)}
          </div>
          <div className="dx-hero-delta">
            <span className={`b ${heroPnl >= 0 ? "" : "down"}`}>
              {heroPnl >= 0 ? "▲" : "▼"} {fmtUsd(heroPnl)}
            </span>
            spread + bonus farkı
          </div>
          <div className="dx-hero-stats">
            <div className="dx-hero-stat">
              <span className="k">Bekleyen bonus</span>
              <span className="v acc" data-testid="hero-pending-bonus">
                ${pendingBonus.toFixed(2)}
              </span>
            </div>
            <div className="dx-hero-stat">
              <span className="k">Açık pozisyon</span>
              <span className="v" data-testid="hero-open-positions">
                {positions.length}
                {positions[0] ? ` · ${positions[0].symbol}` : ""}
              </span>
            </div>
            <div className="dx-hero-stat">
              <span className="k">Tier oranı</span>
              <span className="v">{tierLabel} · %{bonusPct}</span>
            </div>
          </div>
        </div>
        <div className="dx-hero-r">
          <div className="dx-ch-top">
            <div className="t">Net K/Z akışı <span>· bugün</span></div>
            <div className="leg">
              <span><i style={{ background: "var(--up)" }} /> kümülatif</span>
            </div>
          </div>
          <div className="dx-hero-chart">
            <EquityMiniChart pnl={heroPnl} />
          </div>
          <div className="dx-ch-foot">
            <span>09:30</span>
            <span>canlı</span>
            <span>şimdi</span>
          </div>
        </div>
      </section>

      {/* ===== Bugünkü Hedef (Plan vs Gerçek) ===== */}
      <DailyTargetCard />

      {/* ===== Active Hedge ===== */}
      <div className="dx-sec">
        <div className="l">
          <span className="ic">
            <svg viewBox="0 0 24 24">
              <path d="M3 12h3l2-7 4 14 2-7h7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <h2>Aktif Hedge</h2>
          <span className="cnt">{positions.length} canlı</span>
        </div>
        {positions.length > 0 && (
          <Link to="/positions" className="all" data-testid="section-link-positions">
            Tümü →
          </Link>
        )}
      </div>

      {positions.length === 0 ? (
        <div className="dx-empty" data-testid="empty-positions">
          Aktif hedge pozisyonu yok. Aşağıdaki sinyallerden veya{" "}
          <b>Sinyaller</b> sayfasından açabilirsin.
        </div>
      ) : (
        positions.map((p) => (
          <HedgeCard key={p.id} position={p} currentPrices={prices} />
        ))
      )}

      {/* ===== Signals ===== */}
      <div className="dx-sec" style={{ marginTop: 24 }}>
        <div className="l">
          <span className="ic">
            <svg viewBox="0 0 24 24">
              <path d="M13 2 4.5 13.5H11L9.5 22 19 9.5h-6.5L13 2z" strokeLinejoin="round" />
            </svg>
          </span>
          <h2>Öne Çıkan Fırsatlar</h2>
          <span className="cnt">{topSignals.length} sinyal</span>
        </div>
        <Link to="/signals" className="all" data-testid="section-link-signals">
          Tümü →
        </Link>
      </div>

      <section className="dx-signals" data-testid="dashboard-signals">
        {liveLoading && (
          <>
            <SignalSkeleton />
            <SignalSkeleton />
            <SignalSkeleton />
          </>
        )}
        {!liveLoading && topSignals.length === 0 && (
          <div className="dx-empty" style={{ gridColumn: "1 / -1" }}>
            Şu an öne çıkan fırsat yok.{" "}
            <button
              className="dx-sig-go"
              style={{ marginTop: 12 }}
              data-testid="scan-signals-btn"
              onClick={() => scanMut.mutate()}
              disabled={scanMut.isPending}
            >
              {scanMut.isPending ? "Taranıyor…" : "Tarama Yap"}
            </button>
          </div>
        )}
        {topSignals.map((s) => (
          <SignalCard key={s.symbol} signal={s} onOpen={openSignal} />
        ))}
      </section>

      {/* ===== Klines chart + Spread ===== */}
      <div className="dx-extras" data-testid="dashboard-extras">
        <PriceChart
          symbol={settings?.signal_symbol || "ETHUSDT"}
          height={300}
          defaultInterval="minute15"
          priceLines={positions[0]?.darkex_entry ? [
            {
              price: positions[0].darkex_entry,
              color: "var(--acc)",
              title: `${positions[0].symbol} giriş`,
            },
            positions[0].tp_price_counter && {
              price: positions[0].tp_price_counter,
              color: "#2bd98a",
              title: "TP",
            },
            positions[0].sl_price_counter && {
              price: positions[0].sl_price_counter,
              color: "#ff5662",
              title: "SL",
            },
          ].filter(Boolean) : []}
        />
        <SpreadHistogram symbol={settings?.signal_symbol || "ETHUSDT"} />
      </div>

      {/* ===== Status bar ===== */}
      <footer className="dx-statusbar" data-testid="dashboard-status">
        <div className="g">
          <span><span className="d" /> Çevrimiçi</span>
          <span>Darkex <b>{prices?.darkex?.price ? "bağlı" : "bekleniyor"}</b></span>
          <span>
            {(settings?.preferred_counter_exchange || "Binance")} {" "}
            <b>{prices && prices[(settings?.preferred_counter_exchange || "binance").toLowerCase()]?.price ? "bağlı" : "bekleniyor"}</b>
          </span>
          <span>Gecikme <b>~15ms</b></span>
        </div>
        <div className="g dev">
          <span>v2.4.1</span>
          <span>Developed by <b>PARANOYA</b></span>
        </div>
      </footer>
    </div>
  );
}

/* ============================================================
   Mini equity chart — synthetic curve based on stats.total_net_pnl
   ============================================================ */
function EquityMiniChart({ pnl }) {
  const isUp = (pnl || 0) >= 0;
  const color = isUp ? "var(--up)" : "var(--down)";
  // Build a smooth synthetic curve ending at the current pnl value.
  const points = 48;
  const path = useMemo(() => {
    const arr = [];
    const W = 560, H = 180;
    const endY = isUp ? 22 : 158;
    for (let i = 0; i < points; i++) {
      const t = i / (points - 1);
      const x = t * W;
      const noise = Math.sin(i * 0.6) * 8 + Math.cos(i * 0.3) * 5;
      const y = 90 - (endY - 90) * Math.pow(t, 1.8) + noise * (1 - t * 0.6);
      arr.push([x, Math.max(8, Math.min(170, y))]);
    }
    return arr;
  }, [isUp]);
  const linePath = "M" + path.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" L ");
  const fillPath = `${linePath} L 560 180 L 0 180 Z`;
  const lastY = path[path.length - 1][1];
  return (
    <svg viewBox="0 0 560 180" preserveAspectRatio="none" data-testid="equity-mini-chart">
      <defs>
        <linearGradient id="dxgrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity=".24" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" y1="64.6" x2="560" y2="64.6" stroke="rgba(255,255,255,.04)" strokeWidth="1" />
      <line x1="0" y1="115.3" x2="560" y2="115.3" stroke="rgba(255,255,255,.04)" strokeWidth="1" />
      <path d={fillPath} fill="url(#dxgrad)" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <circle cx="560" cy={lastY} r="3.4" fill={color} />
      <circle cx="560" cy={lastY} r="6.5" fill="none" stroke={color} strokeWidth="1" opacity=".35" />
    </svg>
  );
}

/* ============================================================
   Hedge card
   ============================================================ */
function HedgeCard({ position, currentPrices }) {
  const darkexUpnl = Number(position.darkex_upnl ?? 0);
  const counterUpnl = Number(position.counter_upnl ?? 0);
  const netResult = darkexUpnl + counterUpnl;

  const darkexLong = (position.darkex_side || "").toUpperCase() === "LONG";
  const darkexEntry = Number(position.darkex_entry || 0);
  const counterEntry = Number(position.counter_entry || 0);
  const counterExName = position.counter_exchange || "Binance";
  const counterExLower = counterExName.toLowerCase();

  const darkexCurr = Number(currentPrices?.darkex?.price || darkexEntry);
  const counterCurr = Number(currentPrices?.[counterExLower]?.price || counterEntry);

  const qty = Number(position.qty || position.darkex_qty || 0);
  const lev = Number(position.leverage || 1);

  const darkexWinning = darkexUpnl >= 0;

  return (
    <div className="dx-hedge" data-testid={`hedge-${position.id}`}>
      <div className="dx-hedge-h">
        <div className="l">
          <CoinIcon symbol={position.symbol} size="md" />
          <span className="sym">{position.symbol}</span>
          <span className="lev">{lev}× · {qty.toFixed(4)}</span>
          <span className="dx-chip live"><span className="d" /> Canlı</span>
          {darkexWinning && (
            <span className="dx-chip risk">
              <svg viewBox="0 0 24 24">
                <path d="M12 9v4M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" strokeLinejoin="round" />
              </svg>
              Bonus riski · Darkex kârda
            </span>
          )}
        </div>
        <div className="r">
          <div className="dx-hnet">
            <div className="k">Net Sonuç</div>
            <div className={`v ${netResult >= 0 ? "up" : "down"}`}>
              {fmtUsd(netResult)}
            </div>
          </div>
        </div>
      </div>
      <div className="dx-legs">
        <HedgeLeg
          name={counterExName.charAt(0).toUpperCase() + counterExName.slice(1)}
          side={darkexLong ? "short" : "long"}
          entry={counterEntry}
          curr={counterCurr}
          pnl={counterUpnl}
          winning={counterUpnl >= 0}
        />
        <div className="dx-leg-mid"><div className="eq">=</div></div>
        <HedgeLeg
          name="Darkex"
          side={darkexLong ? "long" : "short"}
          entry={darkexEntry}
          curr={darkexCurr}
          pnl={darkexUpnl}
          winning={darkexUpnl >= 0}
        />
      </div>
      <div className="dx-hedge-f">
        <svg viewBox="0 0 24 24">
          <path d="M12 9v4M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" strokeLinejoin="round" />
        </svg>
        Bonus için Darkex bacağı <b>zararda</b> kapanmalı ·{" "}
        {darkexUpnl < 0 ? "şu an zararda ✓" : "şu an kârda"}
      </div>
    </div>
  );
}

function HedgeLeg({ name, side, entry, curr, pnl, winning }) {
  const isLong = side === "long";
  return (
    <div className={`dx-leg ${winning ? "win" : "lose"}`}>
      <div className="dx-leg-top">
        <div className="dx-leg-ex">
          <div className="nm">{name}</div>
          <div className="st">{winning ? "Kazanan" : "Kaybeden"} bacak · İzole</div>
        </div>
        <span className={`dx-pbadge ${isLong ? "long" : "short"}`}>
          {isLong ? "▲ Long" : "▼ Short"}
        </span>
      </div>
      <div className="dx-leg-px num">${entry.toFixed(2)}</div>
      <div className="dx-leg-now">
        giriş · şu an <b className="num">${curr.toFixed(2)}</b>
      </div>
      <div className={`dx-leg-pnl ${pnl >= 0 ? "up" : "down"}`}>{fmtUsd(pnl)}</div>
    </div>
  );
}

/* ============================================================
   Signal card with circular gauge
   ============================================================ */
function SignalCard({ signal, onOpen }) {
  const isLong = (signal.direction || "").toUpperCase() === "LONG";
  const confidence = Math.round(signal.confidence || 0);
  const dir = isLong ? "Long" : "Short";
  const arrow = isLong ? "▲" : "▼";
  const entry = signal.entry_price ?? signal.entry ?? 0;
  const stop = signal.stop_loss ?? signal.stop ?? 0;
  const target = signal.take_profit ?? signal.target ?? 0;
  const rr = signal.risk_reward || 2.5;
  const distancePct = entry && target ? ((Math.abs(target - entry) / entry) * 100) : 0;

  // Gauge: 125.7 = 2*pi*20 (full circumference)
  const dash = 125.7;
  const offset = dash - (dash * confidence) / 100;
  const isHigh = confidence >= 60;
  const gaugeColor = isHigh ? "var(--up)" : confidence >= 40 ? "var(--gold)" : "var(--down)";
  const labelColor = gaugeColor;

  return (
    <article className={`dx-sig ${isLong ? "long" : "short"}`}
             data-testid={`sig-${signal.symbol}`}>
      <div className="dx-sig-top">
        <div className="dx-sig-id">
          <div className="dx-sig-ic">
            {isLong ? (
              <svg viewBox="0 0 24 24">
                <path d="M4 17l5-5 4 3 7-8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 11V7h-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24">
                <path d="M4 7l5 5 4-3 7 8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 13v4h-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div>
            <div className="nm">{signal.symbol}</div>
            <div className="dr">{isLong ? "UP" : "DOWN"} · Long / Short</div>
          </div>
        </div>
        <span className="dx-dbadge">{arrow} {dir}</span>
      </div>

      <div className="dx-sig-levels">
        <div className="lv">
          <div className="k">Giriş</div>
          <div className="v">{fmtPrice(entry)}</div>
        </div>
        <div className="lv s">
          <div className="k">Stop</div>
          <div className="v">{fmtPrice(stop)}</div>
        </div>
        <div className="lv t">
          <div className="k">Hedef</div>
          <div className="v">{fmtPrice(target)}</div>
        </div>
      </div>

      <div className="dx-sig-bot">
        <div className="dx-sig-conf">
          <div className="dx-gauge">
            <svg viewBox="0 0 50 50">
              <circle className="tr" cx="25" cy="25" r="20" />
              <circle
                className="vl" cx="25" cy="25" r="20"
                stroke={gaugeColor}
                strokeDasharray={dash}
                strokeDashoffset={offset}
              />
            </svg>
            <div className="lab" style={{ color: labelColor }}>{confidence}</div>
          </div>
          <div className="dx-conf-m">
            <div className="cl">Güven · R/R</div>
            <div className="rr">1 : {Number(rr).toFixed(2)}</div>
            <div className="ex">Beklenen +{distancePct.toFixed(2)}%</div>
          </div>
        </div>
        <button className="dx-sig-go"
                data-testid={`open-pos-${signal.symbol}`}
                onClick={() => onOpen(signal)}>
          <svg viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
          Pozisyon Aç
        </button>
      </div>
    </article>
  );
}

function SignalSkeleton() {
  return (
    <article className="dx-sig" style={{ opacity: 0.4 }}>
      <div className="dx-sig-top">
        <div className="dx-sig-id">
          <div className="dx-sig-ic" />
          <div>
            <div className="nm" style={{ background: "rgba(255,255,255,0.05)",
              color: "transparent", borderRadius: 4 }}>—————</div>
          </div>
        </div>
      </div>
      <div className="dx-sig-levels">
        <div className="lv"><div className="k">Giriş</div><div className="v">—</div></div>
        <div className="lv s"><div className="k">Stop</div><div className="v">—</div></div>
        <div className="lv t"><div className="k">Hedef</div><div className="v">—</div></div>
      </div>
    </article>
  );
}
